/**
 * The themes: a whole design language for the interface. A theme gives
 *  - two palettes, light and dark (CSS custom properties: surfaces, text, panels, buttons…);
 *  - its look (`appearance`): which variant each module and widget uses, chat size, scheme;
 *  - the preset it starts with.
 *
 * The domain pack adds only its brand (the accent colours, DomainPack.theme), so the same theme
 * dresses trains and bank alike. To change a colour of a theme: change it here, nothing else.
 *
 * A dark palette derives from the brand what must read on a dark surface (DARK_INKS): one lighter
 * accent for buttons, icons and titles alike (two reds on one screen look wrong), the danger colour
 * as text, and the soft tints a little stronger.
 */
import type { Appearance } from '../core/appearance';

export type Palette = Record<string, string>;

/** The brand's colours made readable on a dark surface: any accent, any domain. */
const DARK_INKS: Palette = {
  '--color-accent': 'color-mix(in oklab, var(--brand-accent) 80%, #fff)',
  '--color-accent-strong': 'var(--brand-accent)',
  '--color-accent-soft': 'color-mix(in srgb, var(--brand-accent) 26%, transparent)',
  '--color-accent-ink': 'var(--color-accent)',
  '--color-accent-ink-strong': 'var(--color-accent)',
  '--color-danger-ink': '#ff8f82',
  '--color-danger-soft': 'rgb(255 110 95 / 0.14)',
  // the text scene's light (tokens.css --scene-text-background): a hint of it, not a white spot on a dark page
  '--scene-glow': 'rgb(255 255 255 / 0.06)',
};

export interface Theme {
  id: string;
  /** i18n key */
  label: string;
  /** The preset it starts with; none = the domain pack's (DomainPack.defaultPreset). */
  defaultPreset?: string;
  /** The layouts it is drawn for, by preset id; none: every preset but another theme's own (presetsFor). */
  presets?: readonly string[];
  appearance: Partial<Appearance>;
  palettes: { light: Palette; dark: Palette };
}

/** The look of the old kiosk: warm cream, red headers, tiles, bubbles beside the avatar. */
export const CLASSICO: Theme = {
  id: 'classico',
  label: 'theme.classico',
  appearance: {
    scheme: 'light',
    faceFallback: 'idle',
    moduleVariants: { chat: 'standard', composer: 'standard', 'main-bar': 'standard', progress: 'standard', status: 'standard', avatar: 'orb' },
  },
  palettes: {
    light: {
      '--color-page': '#0e0f12',
      '--color-surface': '#f7f2ea',
      '--color-surface-2': '#ede4d8',
      '--color-surface-glass': 'rgb(247 242 234 / 0.88)',
      '--color-line': '#e0d4c4',
      '--color-text': '#333333',
      '--color-text-muted': '#6b6259',
      '--color-bubble-agent': 'rgb(247 242 234 / 0.88)',
      '--scene-plain': 'linear-gradient(160deg, #f7f2ea 0%, #ede4d8 100%)',
      '--panel-header-bg': 'var(--color-accent)',
      '--panel-header-fg': 'var(--color-on-accent)',
      '--panel-radius': '12px',
      '--inner-radius': '12px',
      '--panel-shadow': '0 8px 25px rgb(0 0 0 / 0.15)',
      '--panel-border': 'none',
    },
    dark: {
      ...DARK_INKS,
      '--color-page': '#0b0a09',
      '--color-surface': '#1f1b19',
      '--color-surface-2': '#2a2522',
      '--color-surface-glass': 'rgb(31 27 25 / 0.9)',
      '--color-line': '#3a332e',
      '--color-text': '#f2ece6',
      '--color-text-muted': '#b3a89e',
      '--color-bubble-agent': 'rgb(31 27 25 / 0.9)',
      '--scene-plain': 'linear-gradient(160deg, #1f1b19 0%, #121010 100%)',
      '--panel-header-bg': 'var(--color-accent)',
      '--panel-header-fg': 'var(--color-on-accent)',
      '--panel-radius': '12px',
      '--inner-radius': '12px',
      '--panel-shadow': '0 8px 25px rgb(0 0 0 / 0.45)',
      '--panel-border': 'none',
    },
  },
};

/** Minimal: one column, the assistant's words large in the middle, pill buttons, few elements. */
export const ESSENZIALE: Theme = {
  id: 'essenziale',
  label: 'theme.essenziale',
  defaultPreset: 'essenziale',
  presets: ['essenziale'], // one column: the lyrics and the pill composer make no sense beside a scene
  appearance: {
    scheme: 'dark',
    chatAlign: 'center',
    panelPadding: 24,
    framePadding: 32,
    faceFallback: 'particles',
    moduleVariants: { chat: 'lyrics', composer: 'pill', 'main-bar': 'pills', progress: 'minimal', status: 'minimal', avatar: 'orb' },
    preferredVariants: ['single', 'card'], // one item at a time, a ticket as a card: whichever widget has them
  },
  palettes: {
    dark: {
      ...DARK_INKS,
      '--color-page': '#000000',
      '--color-surface': '#161616',
      '--color-surface-2': '#222222',
      '--color-surface-glass': 'rgb(22 22 22 / 0.85)',
      '--color-line': '#333333',
      '--color-text': '#f4f4f4',
      '--color-text-muted': '#9b9b9b',
      '--color-bubble-agent': 'transparent',
      '--scene-plain': 'radial-gradient(120% 70% at 50% 0%, #1c1c1c 0%, #0a0a0a 60%, #000 100%)',
      '--panel-header-bg': 'transparent',
      '--panel-header-padding': 'var(--panel-padding) var(--panel-padding) 0',
      '--panel-header-fg': 'var(--color-text)',
      '--panel-radius': '32px',
      '--inner-radius': '22px',
      '--panel-shadow': 'none',
      '--panel-border': '1px solid #2a2a2a',
    },
    light: {
      '--color-page': '#e9e9e6',
      '--color-surface': '#ffffff',
      '--color-surface-2': '#f1f1ef',
      '--color-surface-glass': 'rgb(255 255 255 / 0.9)',
      '--color-line': '#e0e0dc',
      '--color-text': '#141414',
      '--color-text-muted': '#6d6d6d',
      '--color-bubble-agent': 'transparent',
      '--scene-plain': 'radial-gradient(120% 70% at 50% 0%, #ffffff 0%, #f2f2ef 60%, #e9e9e6 100%)',
      '--panel-header-bg': 'transparent',
      '--panel-header-padding': 'var(--panel-padding) var(--panel-padding) 0',
      '--panel-header-fg': 'var(--color-text)',
      '--panel-radius': '32px',
      '--inner-radius': '22px',
      '--panel-shadow': '0 10px 40px rgb(0 0 0 / 0.06)',
      '--panel-border': '1px solid #e6e6e2',
    },
  },
};

export const BUILTIN_THEMES: readonly Theme[] = [CLASSICO, ESSENZIALE];
export const DEFAULT_THEME_ID = CLASSICO.id;

export function themeById(id: string | undefined | null): Theme {
  return BUILTIN_THEMES.find((t) => t.id === id) ?? CLASSICO;
}

/** The preset to start with: the URL's, the one chosen on this device, the theme's, the domain's. */
export function startingPreset(
  candidates: { url?: string | null; saved?: string; theme: Theme; packDefault?: string },
  exists: (id: string) => boolean,
): string {
  for (const id of [candidates.url, candidates.saved, candidates.theme.defaultPreset, candidates.packDefault, 'kiosk']) {
    if (id && exists(id)) return id;
  }
  return 'kiosk';
}

/**
 * The presets a theme offers, in the given order: its own list, else every preset that is not another theme's own.
 * A theme's look on a layout it was not drawn for is a mix nobody chose (Essenziale's lyrics beside the avatar).
 */
export function presetsFor(theme: Theme, presets: readonly string[]): string[] {
  if (theme.presets) return presets.filter((id) => theme.presets!.includes(id));
  const others = new Set(BUILTIN_THEMES.filter((t) => t.id !== theme.id && t.defaultPreset).map((t) => t.defaultPreset!));
  return presets.filter((id) => !others.has(id));
}
